import React from "react";

// CSS animation style for pulsing skeleton
const pulseStyle = `
  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    50% {
      opacity: 0.3;
    }
    100% {
      opacity: 0.6;
    }
  }
  .skeleton-pulse {
    animation: pulse 1.5s ease-in-out infinite;
    background-color: #e5e7eb;
    border-radius: 4px;
  }
`;

export function SkeletonBlock({ width = "100%", height = "20px", style = {} }) {
  return (
    <div
      className="skeleton-pulse"
      style={{
        width,
        height,
        ...style,
      }}
    />
  );
}

// Skeleton for Header Stats (Top Row Cards)
export function HeaderStatsSkeleton() {
  return (
    <>
      <style>{pulseStyle}</style>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "24px",
          width: "100%",
          alignItems: "flex-start",
        }}
      >
        {/* Left Side Cards */}
        <div style={{ flex: "0 0 50%", display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Balance Card Skeleton */}
          <div style={{ background: "#ffffff", border: "1px solid rgba(226, 232, 240, 0.8)", borderRadius: "20px", padding: "22px", height: "135px" }}>
            <div style={{ display: "flex", gap: "20px" }}>
              <SkeletonBlock width="56px" height="56px" style={{ borderRadius: "16px" }} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px", justifyContent: "center" }}>
                <SkeletonBlock width="40%" height="14px" />
                <SkeletonBlock width="70%" height="32px" />
                <SkeletonBlock width="50%" height="12px" />
              </div>
            </div>
          </div>
          {/* Money Cards Skeleton */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px" }}>
            <div style={{ background: "#ffffff", border: "1px solid rgba(226, 232, 240, 0.8)", borderRadius: "16px", padding: "22px", height: "160px", display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <SkeletonBlock width="44px" height="44px" style={{ borderRadius: "12px" }} />
                <SkeletonBlock width="50%" height="16px" />
              </div>
              <SkeletonBlock width="70%" height="26px" />
              <hr style={{ borderTop: "1px solid #f0f0f0", margin: "4px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <SkeletonBlock width="40%" height="14px" />
                <SkeletonBlock width="30%" height="14px" />
              </div>
            </div>
            <div style={{ background: "#ffffff", border: "1px solid rgba(226, 232, 240, 0.8)", borderRadius: "16px", padding: "22px", height: "160px", display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <SkeletonBlock width="44px" height="44px" style={{ borderRadius: "12px" }} />
                <SkeletonBlock width="50%" height="16px" />
              </div>
              <SkeletonBlock width="70%" height="26px" />
              <SkeletonBlock width="80%" height="12px" style={{ marginTop: "auto" }} />
            </div>
          </div>
          {/* Spending & Saving Rate Card */}
          <div style={{ background: "#ffffff", border: "1px solid rgba(226, 232, 240, 0.8)", borderRadius: "16px", padding: "22px", height: "140px", display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <SkeletonBlock width="20px" height="20px" />
              <SkeletonBlock width="60%" height="16px" />
            </div>
            <div style={{ display: "flex", gap: "32px", alignItems: "center" }}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px", alignItems: "center" }}>
                <SkeletonBlock width="70%" height="12px" />
                <SkeletonBlock width="40%" height="32px" />
                <SkeletonBlock width="50%" height="12px" />
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px", alignItems: "center" }}>
                <SkeletonBlock width="70%" height="12px" />
                <SkeletonBlock width="40%" height="32px" />
                <SkeletonBlock width="50%" height="12px" />
              </div>
            </div>
          </div>
        </div>

        {/* Right Side Card (Distribution Pie + breakdown) */}
        <div style={{ flex: "0 0 calc(50% - 24px)", background: "#ffffff", border: "1px solid rgba(226, 232, 240, 0.8)", borderRadius: "24px", padding: "2rem", height: "485px", display: "flex", flexDirection: "column", gap: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px", width: "50%" }}>
              <SkeletonBlock width="80%" height="20px" />
              <SkeletonBlock width="40%" height="14px" />
            </div>
            <SkeletonBlock width="40px" height="40px" style={{ borderRadius: "12px" }} />
          </div>
          <div style={{ display: "grid", gap: "12px" }}>
            <SkeletonBlock width="100%" height="56px" style={{ borderRadius: "16px" }} />
            <SkeletonBlock width="100%" height="56px" style={{ borderRadius: "16px" }} />
            <SkeletonBlock width="100%" height="56px" style={{ borderRadius: "16px" }} />
          </div>
          <div style={{ display: "flex", gap: "2rem", alignItems: "center", marginTop: "10px" }}>
            <SkeletonBlock width="120px" height="120px" style={{ borderRadius: "50%", flexShrink: 0 }} />
            <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <SkeletonBlock width="100%" height="80px" style={{ borderRadius: "16px" }} />
              <SkeletonBlock width="100%" height="80px" style={{ borderRadius: "16px" }} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Skeleton for Budget Stats Cards (Second Row Cards)
export function BudgetStatsCardsSkeleton() {
  return (
    <>
      <style>{pulseStyle}</style>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "20px",
          width: "100%",
          padding: "16px 0 24px 0",
        }}
      >
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              background: "#ffffff",
              border: "1px solid rgba(226, 232, 240, 0.8)",
              borderRadius: "16px",
              padding: "24px",
              height: "172px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <SkeletonBlock width="60%" height="16px" />
              <SkeletonBlock width="36px" height="36px" style={{ borderRadius: "10px" }} />
            </div>
            <SkeletonBlock width="80%" height="28px" />
            <SkeletonBlock width="90%" height="12px" />
            <SkeletonBlock width="100%" height="8px" style={{ borderRadius: "4px", marginTop: "auto" }} />
          </div>
        ))}
      </div>
    </>
  );
}

// Skeleton for Budget Chart (Bottom Row Card)
export function BudgetChartSkeleton() {
  return (
    <>
      <style>{pulseStyle}</style>
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          border: "1px solid rgba(226, 232, 240, 0.8)",
          padding: "24px",
          minHeight: "400px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          width: "100%",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <SkeletonBlock width="150px" height="20px" />
          <div style={{ display: "flex", gap: "10px" }}>
            <SkeletonBlock width="80px" height="30px" style={{ borderRadius: "8px" }} />
            <SkeletonBlock width="80px" height="30px" style={{ borderRadius: "8px" }} />
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "24px", height: "260px", padding: "10px 0" }}>
          {[...Array(12)].map((_, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px", height: "100%", justifyContent: "flex-end" }}>
              <div style={{ display: "flex", gap: "4px", height: "100%", alignItems: "flex-end" }}>
                <SkeletonBlock width="50%" height={`${Math.max(20, Math.random() * 90)}%`} style={{ borderTopLeftRadius: "4px", borderTopRightRadius: "4px" }} />
                <SkeletonBlock width="50%" height={`${Math.max(20, Math.random() * 90)}%`} style={{ borderTopLeftRadius: "4px", borderTopRightRadius: "4px" }} />
              </div>
              <SkeletonBlock width="100%" height="10px" />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// Skeleton for Annual View Header Cards
export function AnnualHeaderStatsSkeleton() {
  return (
    <>
      <style>{pulseStyle}</style>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "20px", width: "100%", padding: "16px 0 20px 0" }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ background: "#ffffff", border: "1px solid rgba(226, 232, 240, 0.8)", borderRadius: "16px", padding: "22px", height: "148px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <SkeletonBlock width="36px" height="36px" style={{ borderRadius: "10px" }} />
              <SkeletonBlock width="50%" height="16px" />
            </div>
            <SkeletonBlock width="70%" height="28px" />
            <SkeletonBlock width="80%" height="12px" style={{ marginTop: "auto" }} />
          </div>
        ))}
        {/* Rate Card */}
        <div style={{ background: "#ffffff", border: "1px solid rgba(226, 232, 240, 0.8)", borderRadius: "16px", padding: "22px", height: "148px", display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <SkeletonBlock width="36px" height="36px" style={{ borderRadius: "10px" }} />
            <SkeletonBlock width="60%" height="16px" />
          </div>
          <div style={{ display: "flex", gap: "16px", alignItems: "center", marginTop: "auto" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
              <SkeletonBlock width="100%" height="10px" />
              <SkeletonBlock width="60%" height="20px" />
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
              <SkeletonBlock width="100%" height="10px" />
              <SkeletonBlock width="60%" height="20px" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Skeleton for Annual Report Table
export function AnnualTableSkeleton() {
  return (
    <>
      <style>{pulseStyle}</style>
      <div style={{ background: "#ffffff", border: "1px solid rgba(226, 232, 240, 0.8)", borderRadius: "20px", padding: "24px", width: "100%", display: "flex", flexDirection: "column", gap: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <SkeletonBlock width="200px" height="22px" />
          <SkeletonBlock width="120px" height="32px" style={{ borderRadius: "8px" }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", gap: "20px" }}>
            {[...Array(6)].map((_, i) => <SkeletonBlock key={i} width="16%" height="24px" />)}
          </div>
          <hr style={{ borderTop: "1px solid #f0f0f0", margin: "4px 0" }} />
          {[...Array(8)].map((_, rowIndex) => (
            <div key={rowIndex} style={{ display: "flex", gap: "20px" }}>
              {[...Array(6)].map((_, colIndex) => <SkeletonBlock key={colIndex} width="16%" height="20px" />)}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// Skeleton layout for the 5 budget planning cards
export function BudgetTrackerSkeleton() {
  return (
    <>
      <style>{pulseStyle}</style>
      <div style={{ padding: "0 90px", margin: 0, width: "100%" }}>
        <div className="row" style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", alignItems: "stretch" }}>
          <div className="col-12" style={{ marginBottom: "12px" }}></div>
          {[...Array(5)].map((_, i) => (
            <React.Fragment key={i}>
              <div className="custom-col-md-2 col-6" style={{ marginBottom: "6px", height: "100%" }}>
                <div
                  style={{
                    background: "#ffffff",
                    border: "1px solid rgba(226, 232, 240, 0.8)",
                    borderRadius: "16px",
                    padding: "16px",
                    height: "100px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <SkeletonBlock width="32px" height="32px" style={{ borderRadius: "8px" }} />
                    <SkeletonBlock width="50%" height="14px" />
                  </div>
                  <SkeletonBlock width="40%" height="24px" />
                  <SkeletonBlock width="60%" height="12px" />
                </div>
              </div>
              {i < 4 && (
                <div style={{ width: "20px", display: "flex", alignItems: "center", justifyContent: "center" }} className="hide-custom">
                  <SkeletonBlock width="12px" height="12px" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  );
}

// Skeleton for Product Expense Table (Business View)
export function ProductTableSkeleton() {
  return (
    <>
      <style>{pulseStyle}</style>
      <div style={{ background: "#ffffff", border: "1px solid rgba(226, 232, 240, 0.8)", borderRadius: "16px", padding: "24px", margin: "24px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <SkeletonBlock width="180px" height="24px" />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div style={{ display: "flex", gap: "16px" }}>
            <SkeletonBlock width="250px" height="20px" style={{ flexShrink: 0 }} />
            {[...Array(8)].map((_, i) => <SkeletonBlock key={i} width="10%" height="20px" />)}
          </div>
          <hr style={{ borderTop: "1px solid #f0f0f0", margin: "4px 0" }} />
          {[...Array(6)].map((_, rowIndex) => (
            <div key={rowIndex} style={{ display: "flex", gap: "16px" }}>
              <SkeletonBlock width="250px" height="18px" style={{ flexShrink: 0 }} />
              {[...Array(8)].map((_, colIndex) => <SkeletonBlock key={colIndex} width="10%" height="18px" />)}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// Skeleton for Cashflow Statement Table
export function CashflowStatementSkeleton() {
  return (
    <>
      <style>{pulseStyle}</style>
      <div style={{ background: "#ffffff", border: "1px solid rgba(226, 232, 240, 0.8)", borderRadius: "16px", padding: "24px", margin: "20px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <SkeletonBlock width="220px" height="24px" />
        </div>
        <div style={{ display: "flex", border: "1px solid rgba(226, 232, 240, 0.8)", borderRadius: "12px", overflow: "hidden" }}>
          {/* Left particulars column */}
          <div style={{ width: "280px", borderRight: "2px solid #e2e8f0", background: "#f8fafc", padding: "16px", display: "flex", flexDirection: "column", gap: "20px" }}>
            <SkeletonBlock width="80%" height="18px" />
            <SkeletonBlock width="90%" height="18px" />
            <SkeletonBlock width="70%" height="18px" />
            <SkeletonBlock width="85%" height="18px" />
            <SkeletonBlock width="75%" height="18px" />
          </div>
          {/* Right months columns */}
          <div style={{ flex: 1, padding: "16px", display: "flex", gap: "24px", overflow: "hidden" }}>
            {[...Array(6)].map((_, colIndex) => (
              <div key={colIndex} style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px" }}>
                <SkeletonBlock width="100%" height="18px" />
                <SkeletonBlock width="100%" height="18px" />
                <SkeletonBlock width="100%" height="18px" />
                <SkeletonBlock width="100%" height="18px" />
                <SkeletonBlock width="100%" height="18px" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
// Skeleton for the Expense Tab (ThreeCards + ExpenseStatement table + charts)
export function ExpenseTabSkeleton() {
  return (
    <>
      <style>{pulseStyle}</style>

      {/* Three summary cards row */}
      <div style={{ padding: "0 90px", marginTop: "20px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px", marginBottom: "32px" }}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                background: "#ffffff",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                borderRadius: "16px",
                padding: "24px",
                height: "120px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <SkeletonBlock width="50%" height="14px" />
                <SkeletonBlock width="36px" height="36px" style={{ borderRadius: "10px" }} />
              </div>
              <SkeletonBlock width="60%" height="28px" />
              <SkeletonBlock width="80%" height="10px" />
            </div>
          ))}
        </div>

        {/* Expense statement table skeleton */}
        <div
          style={{
            background: "#ffffff",
            border: "1px solid rgba(226, 232, 240, 0.8)",
            borderRadius: "16px",
            padding: "24px",
            marginBottom: "32px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <SkeletonBlock width="200px" height="22px" />
            <div style={{ display: "flex", gap: "8px" }}>
              <SkeletonBlock width="80px" height="30px" style={{ borderRadius: "8px" }} />
              <SkeletonBlock width="80px" height="30px" style={{ borderRadius: "8px" }} />
            </div>
          </div>
          {/* Table header */}
          <div style={{ display: "flex", gap: "16px", paddingBottom: "12px", borderBottom: "1px solid #f0f0f0" }}>
            {[30, 15, 15, 15, 15, 10].map((w, i) => (
              <SkeletonBlock key={i} width={`${w}%`} height="16px" />
            ))}
          </div>
          {/* Table rows */}
          {[1, 2, 3, 4, 5, 6].map((row) => (
            <div key={row} style={{ display: "flex", gap: "16px", paddingBottom: "10px", borderBottom: "1px solid #f9fafb" }}>
              {[30, 15, 15, 15, 15, 10].map((w, i) => (
                <SkeletonBlock key={i} width={`${w}%`} height="14px" />
              ))}
            </div>
          ))}
        </div>

        {/* Charts row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "32px" }}>
          {[1, 2].map((i) => (
            <div
              key={i}
              style={{
                background: "#ffffff",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                borderRadius: "16px",
                padding: "24px",
                height: "280px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <SkeletonBlock width="150px" height="18px" />
                <SkeletonBlock width="60px" height="28px" style={{ borderRadius: "8px" }} />
              </div>
              {/* Chart area */}
              <div style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: "10px" }}>
                {[...Array(8)].map((_, j) => (
                  <SkeletonBlock
                    key={j}
                    width="12%"
                    height={`${30 + ((j * 17) % 70)}%`}
                    style={{ borderTopLeftRadius: "4px", borderTopRightRadius: "4px" }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "40px" }}>
          {[1, 2].map((i) => (
            <div
              key={i}
              style={{
                background: "#ffffff",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                borderRadius: "16px",
                padding: "24px",
                height: "260px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <SkeletonBlock width="160px" height="18px" />
              <SkeletonBlock width="100%" height="12px" style={{ borderRadius: "6px", marginTop: "8px" }} />
              <div style={{ flex: 1, display: "flex", gap: "12px", alignItems: "flex-end" }}>
                {[...Array(6)].map((_, j) => (
                  <div key={j} style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px", alignItems: "center" }}>
                    <SkeletonBlock width="100%" height={`${40 + ((j * 19) % 80)}px`} style={{ borderTopLeftRadius: "4px", borderTopRightRadius: "4px" }} />
                    <SkeletonBlock width="80%" height="10px" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
// Skeleton for the Products Tab cards grid
export function ProductCardsGridSkeleton() {
  return (
    <>
      <style>{pulseStyle}</style>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          width: "100%",
          gap: "4rem",
          marginTop: "10rem",
        }}
      >
        {[1, 2, 3].map((cardIndex) => (
          <div
            key={cardIndex}
            style={{
              display: "flex",
              flexDirection: "column",
              padding: "30px",
              borderRadius: "20px",
              background: "#ffffff",
              border: "1px solid rgba(0, 128, 122, 0.08)",
              width: "100%",
              minWidth: 0,
              gap: "25px",
            }}
          >
            {/* Header: Title & description/action skeleton */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <SkeletonBlock width="40%" height="24px" />
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <SkeletonBlock width="60px" height="28px" style={{ borderRadius: "6px" }} />
                <SkeletonBlock width="34px" height="34px" style={{ borderRadius: "50%" }} />
                <SkeletonBlock width="34px" height="34px" style={{ borderRadius: "50%" }} />
              </div>
            </div>

            {/* Grid of detail item skeletons */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  style={{
                    background: "linear-gradient(135deg, #f8fafc, #edf2f7)",
                    padding: "16px 20px",
                    borderRadius: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  <SkeletonBlock width="60%" height="10px" />
                  <SkeletonBlock width="40%" height="16px" />
                </div>
              ))}
            </div>

            {/* See more toggle skeleton */}
            <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
              <SkeletonBlock width="32px" height="32px" style={{ borderRadius: "50%" }} />
            </div>

            {/* Calculate Button skeleton */}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <SkeletonBlock width="200px" height="54px" style={{ borderRadius: "50px" }} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
